import React, { useEffect, useState } from 'react';
import api, { isSupervisorRole, getCurrentUser } from '../api/expoApi';
import HubSelector from '../components/HubSelector';

export default function KpiDashboard() {
    const user = getCurrentUser();
    const isSupervisor = isSupervisorRole(user?.role);
    const [definitions, setDefinitions] = useState([]);
    const [entries, setEntries] = useState([]);
    const [hubId, setHubId] = useState(isSupervisor ? null : (user?.hub_id || user?.hubId));
    const [tab, setTab] = useState('dashboard');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [dashData, setDashData] = useState([]);
    const [newKpi, setNewKpi] = useState({ name: '', code: '', unit: 'COUNT', period: 'DAILY', target_value: '', direction: 'UP' });
    const [newEntry, setNewEntry] = useState({ kpi_id: '', actual_value: '', notes: '' });
    const [msg, setMsg] = useState('');

    const load = async () => {
        try {
            const [defs, ents, dash] = await Promise.all([
                api.get('/kpis/definitions'),
                api.get('/kpis/entries'),
                api.get('/kpis/dashboard', { params: hubId ? { date, hub_id: hubId } : { date } })
            ]);
            setDefinitions(defs.data?.data || []);
            setEntries(ents.data?.data || []);
            setDashData(dash.data?.data || []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [hubId, date]);

    const createKpi = async () => {
        try {
            await api.post('/kpis/definitions', { ...newKpi, hub_id: hubId });
            setNewKpi({ name: '', code: '', unit: 'COUNT', period: 'DAILY', target_value: '', direction: 'UP' });
            setMsg('KPI created');
            load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const recordEntry = async () => {
        try {
            await api.post('/kpis/entries', { ...newEntry, hub_id: hubId, period_date: date });
            setNewEntry({ kpi_id: '', actual_value: '', notes: '' });
            setMsg('Entry recorded');
            load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    const autoCompute = async () => {
        if (!isSupervisor) return;
        try {
            const r = await api.post('/kpis/auto-compute', { date });
            setMsg(`Auto-computed ${r.data?.data?.computed?.length || 0} entries`);
            load();
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    return (
        <div style={{ padding: 24 }}>
            <h1>KPI Management</h1>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <HubSelector value={hubId} onChange={setHubId} />
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                {isSupervisor && <button onClick={autoCompute} style={{ background: '#0d6efd', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 4 }}>
                    Auto-compute system KPIs
                </button>}
                {msg && <span style={{ color: '#198754' }}>{msg}</span>}
            </div>

            <div style={{ borderBottom: '1px solid #ddd', marginBottom: 16 }}>
                {['dashboard', 'definitions', 'entries'].map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        style={{
                            border: 'none', padding: '10px 16px',
                            background: tab === t ? '#fff' : 'transparent',
                            borderBottom: tab === t ? '2px solid #0d6efd' : 'none',
                            cursor: 'pointer', textTransform: 'capitalize'
                        }}>
                        {t}
                    </button>
                ))}
            </div>

            {tab === 'dashboard' && (
                <table style={tableStyle}>
                    <thead><tr><th>KPI</th><th>Hub</th><th>Actual</th><th>Target</th><th>Achievement</th></tr></thead>
                    <tbody>
                        {dashData.map((k, i) => (
                            <tr key={i}>
                                <td>{k.name}</td>
                                <td>{k.hub_code || '—'}</td>
                                <td>{k.actual_value !== null ? Number(k.actual_value).toLocaleString() : '—'} {k.unit}</td>
                                <td>{k.target_value !== null ? Number(k.target_value).toLocaleString() : '—'}</td>
                                <td>{k.achievement_pct !== null ? `${k.achievement_pct}%` : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {tab === 'definitions' && (
                <>
                    {isSupervisor && (
                        <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 6, marginBottom: 16 }}>
                            <h3>Add KPI Definition</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                <input placeholder="Name" value={newKpi.name} onChange={(e) => setNewKpi({...newKpi, name: e.target.value})} />
                                <input placeholder="Code (e.g. ON_TIME_DELIV)" value={newKpi.code} onChange={(e) => setNewKpi({...newKpi, code: e.target.value})} />
                                <input placeholder="Unit" value={newKpi.unit} onChange={(e) => setNewKpi({...newKpi, unit: e.target.value})} />
                                <select value={newKpi.period} onChange={(e) => setNewKpi({...newKpi, period: e.target.value})}>
                                    <option>DAILY</option><option>WEEKLY</option><option>MONTHLY</option>
                                </select>
                                <input type="number" placeholder="Target" value={newKpi.target_value} onChange={(e) => setNewKpi({...newKpi, target_value: e.target.value})} />
                                <select value={newKpi.direction} onChange={(e) => setNewKpi({...newKpi, direction: e.target.value})}>
                                    <option value="UP">UP (higher is better)</option>
                                    <option value="DOWN">DOWN (lower is better)</option>
                                </select>
                            </div>
                            <button onClick={createKpi} style={{ marginTop: 8, padding: '8px 16px' }}>Create</button>
                        </div>
                    )}
                    <table style={tableStyle}>
                        <thead><tr><th>Code</th><th>Name</th><th>Hub</th><th>Period</th><th>Target</th><th>Direction</th><th>System</th></tr></thead>
                        <tbody>
                            {definitions.map((d) => (
                                <tr key={d.kpi_id}>
                                    <td><code>{d.code}</code></td>
                                    <td>{d.name}</td>
                                    <td>{d.hub_id ? 'Hub-specific' : 'Global'}</td>
                                    <td>{d.period}</td>
                                    <td>{d.target_value || '—'} {d.unit}</td>
                                    <td>{d.direction}</td>
                                    <td>{d.is_system ? '✓' : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {tab === 'entries' && (
                <>
                    <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 6, marginBottom: 16 }}>
                        <h3>Record Entry</h3>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <select value={newEntry.kpi_id} onChange={(e) => setNewEntry({...newEntry, kpi_id: e.target.value})}>
                                <option value="">Select KPI...</option>
                                {definitions.map((d) => <option key={d.kpi_id} value={d.kpi_id}>{d.name} ({d.code})</option>)}
                            </select>
                            <input type="number" step="0.01" placeholder="Value" value={newEntry.actual_value} onChange={(e) => setNewEntry({...newEntry, actual_value: e.target.value})} />
                            <input placeholder="Notes (optional)" value={newEntry.notes} onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})} />
                            <button onClick={recordEntry}>Record</button>
                        </div>
                    </div>
                    <table style={tableStyle}>
                        <thead><tr><th>Date</th><th>KPI</th><th>Hub</th><th>Value</th><th>Notes</th></tr></thead>
                        <tbody>
                            {entries.slice(0, 100).map((e) => (
                                <tr key={e.entry_id}>
                                    <td>{e.period_date}</td>
                                    <td>{e.kpi_name}</td>
                                    <td>{e.hub_code || '—'}</td>
                                    <td>{Number(e.actual_value).toLocaleString()} {e.unit}</td>
                                    <td>{e.notes}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', background: '#fff' };
